// Pet ("Petopia") header. Deliberately simpler than the grocery header — no
// maps/region switcher/cart-drawer, and no secondary category-nav row
// (browsing lives on the homepage category strip, the /categories page, and
// the mobile bottom tab bar instead). Desktop: logo, centered search,
// utility row (Wishlist / Cart / Account). Mobile: hamburger,
// centered logo, bell, cart badge.
import { useState, useEffect, useRef, useCallback } from 'react';
import { Avatar, Badge, Grid, Dropdown } from 'antd';
import {
  SearchOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  UserSwitchOutlined,
  LogoutOutlined,
  HeartOutlined,
  MenuOutlined,
  CloseOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@core/hooks/useAuth';
import { useAccountSelectionStore } from '@core/store/accountSelectionStore';
import { useActiveCart } from '@core/hooks/useCart';
import { useCategories } from '@core/hooks/useCatalog';
import { useLocations } from '@core/hooks/useLocations';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import { useUiStore } from '@core/store/uiStore';
import { useWishlistStore } from '@core/store/wishlistStore';
import { getAvatarStyles } from '@core/utils/avatar';
import MobileNav from '@components/base/MobileNav';
import StorefrontAuthModal from '@components/base/StorefrontAuthModal';
import CartDrawer from '@components/base/CartDrawer';
import CheckoutModal from '@components/base/CheckoutModal';
import {
  CHECKOUT_MODAL_REDIRECT,
  loadCheckoutModule,
  openCheckoutModal,
} from '@components/base/CheckoutModal/checkoutModalEvents.js';
import { useCopy } from '@/app/providers/useCopy';
import { useIndustry } from '@/app/providers/industry-context.js';
import { useAppLanguage } from '@/app/providers/useAppLanguage.js';

export default function HeaderPet() {
  const t = useCopy();
  const { customer, isAuthenticated, logout } = useAuth();
  const openAccountSelector = useAccountSelectionStore((s) => s.openAccountSelector);
  const { lineCount } = useActiveCart();
  const { config } = useIndustry();
  const { logoUrl: runtimeLogoUrl, brandName: runtimeBrandName } = useRuntimeBrand();
  const { data: locations = [] } = useLocations();
  const { data: categories = [] } = useCategories();
  const { locationId } = useStoreContext();
  const openStoreSelector = useUiStore((s) => s.openStoreSelector);
  const wishlistCount = useWishlistStore((s) => s.items.length);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [navOpen, setNavOpen] = useState(false);
  const [authMode, setAuthMode] = useState(null);
  const [authRedirect, setAuthRedirect] = useState(null);
  const [searchValue, setSearchValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('q') || '';
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const closeCart = useCallback(() => setCartOpen(false), []);

  // Fetch the checkout code while the cart is open so the modal opens fast.
  useEffect(() => {
    if (cartOpen) loadCheckoutModule();
  }, [cartOpen]);
  useAppLanguage();
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const isSearchAllowedPage =
    pathname === '/' ||
    pathname === '/catalog' ||
    pathname === '/categories' ||
    pathname.startsWith('/catalog') ||
    pathname.startsWith('/categories');
  const shouldHideHeaderSearch = !isSearchAllowedPage;

  const navItems = config?.nav?.primary ?? [];
  const collectionItems = categories
    .filter((category) => !category.parentId)
    .map((category) => ({
      label: category.name,
      to: `/catalog?category=${category.id}`,
    }));
  const onOrdersClick = () => {
    if (isAuthenticated) {
      navigate('/orders');
      return;
    }
    setAuthRedirect('/orders');
    setAuthMode('login');
  };
  const desktopNavItems = [
    { label: 'Home', to: '/' },
    { label: 'Category', to: '/categories' },
    { label: 'Products', to: '/catalog' },
    { label: 'Orders', to: '/orders', onClick: onOrdersClick },
  ];
  const utilityItems = config?.nav?.utilities ?? [];
  const appName = runtimeBrandName || config?.copy?.appName || config?.name || 'Petopia';
  const displayName = customer?.first_name || customer?.email?.split('@')[0] || 'Account';
  const activeLocation = locations.find((l) => l.id === locationId) ?? locations[0];

  useEffect(() => {
    if (!location.state?.openCartDrawer) return;
    setCartOpen(true);
    navigate(`${pathname}${search}`, { replace: true, state: null });
  }, [location.state, navigate, pathname, search]);

  const accountItems = [
    { key: 'email', label: customer?.email ?? displayName, disabled: true },
    { type: 'divider' },
    { key: 'profile', icon: <UserOutlined />, label: t('profile', 'Profile') },
    {
      key: 'switch_account',
      icon: <UserSwitchOutlined />,
      label: t('switchAccount', 'Switch Account'),
    },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: t('signOut', 'Sign Out') },
  ];

  const onAccountClick = ({ key }) => {
    if (key === 'profile') navigate('/account');
    if (key === 'switch_account') openAccountSelector();
    if (key === 'logout') {
      logout();
      navigate('/');
    }
  };

  useEffect(() => {
    const handleOpenAuthModal = (e) => {
      const { mode = 'login', redirect = null } = e.detail || {};
      setAuthMode(mode);
      if (redirect) setAuthRedirect(redirect);
    };
    window.addEventListener('open-auth-modal', handleOpenAuthModal);
    return () => window.removeEventListener('open-auth-modal', handleOpenAuthModal);
  }, []);

  useEffect(() => {
    setCollectionsOpen(false);
    setSearchOpen(false);
  }, [pathname, search]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const query = params.get('q') || '';
    if (query !== searchValue) {
      setSearchValue(query);
    }
  }, [search]);

  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      const trimmed = value.trim();
      const targetUrl = trimmed ? `/catalog?q=${encodeURIComponent(trimmed)}` : '/catalog';
      const currentUrl = `${pathname}${search}`;
      if (currentUrl !== targetUrl) {
        navigate(targetUrl);
      }
    }, 1000);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    const trimmed = searchValue.trim();
    navigate(trimmed ? `/catalog?q=${encodeURIComponent(trimmed)}` : '/catalog');
  };

  const UTILITY_ICONS = {
    Wishlist: <HeartOutlined />,
  };
  const isSearchRowAllowedPage =
    pathname === '/' ||
    pathname === '/categories' ||
    pathname.startsWith('/categories/') ||
    pathname === '/catalog' ||
    pathname.startsWith('/catalog/');

  if (isMobile) {
    return (
      <>
        <header className="pet-header pet-header-mobile">
          <div className="pet-header-mobile-left">
            <Link to="/" className="pet-header-logo">
              {runtimeLogoUrl || config?.logoUrl ? (
                <img
                  src={runtimeLogoUrl || config.logoUrl}
                  alt=""
                  style={{
                    width: '2.05rem',
                    height: '2.05rem',
                    objectFit: 'contain',
                    background: 'transparent',
                  }}
                />
              ) : (
                <span className="pet-header-paw" aria-hidden>
                  🐾
                </span>
              )}
            </Link>

            <button
              type="button"
              className={`pet-header-location ${activeLocation ? 'is-selected' : ''}`}
              onClick={openStoreSelector}
            >
              <img src="/location-pin.png" alt="Location" className="location-icon-img" />
              <span>
                <strong>{activeLocation?.name ?? ''}</strong>
              </span>
            </button>
          </div>

          <div className="pet-header-mobile-right">
            {!shouldHideHeaderSearch ? (
              <button
                type="button"
                className={`pet-header-icon-btn pet-header-search-btn ${searchOpen ? 'active' : ''}`}
                onClick={() => setSearchOpen((prev) => !prev)}
                aria-label={t('search', 'Search')}
              >
                <SearchOutlined />
              </button>
            ) : null}

            <Link to="/wishlist" className="pet-header-icon-btn" aria-label="Wishlist">
              <Badge count={wishlistCount} size="small" offset={[-2, 2]}>
                <HeartOutlined />
              </Badge>
            </Link>

            <button
              type="button"
              className="pet-header-icon-btn pet-header-orders-btn"
              aria-label={t('myOrders', 'My Orders')}
              onClick={onOrdersClick}
            >
              <ShoppingOutlined />
            </button>

            <Link to="/cart" className="pet-header-icon-btn pet-header-cart-btn" aria-label="Cart">
              <Badge count={lineCount} size="small" offset={[-2, 2]}>
                <ShoppingCartOutlined />
              </Badge>
            </Link>

            {isAuthenticated ? (
              <Dropdown menu={{ items: accountItems, onClick: onAccountClick }} trigger={['click']}>
                <button type="button" className="pet-header-icon-btn" aria-label="Account">
                  <Avatar
                    size={32}
                    className="pet-header-account-avatar"
                    style={{
                      ...getAvatarStyles(displayName),
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {displayName[0]?.toUpperCase()}
                  </Avatar>
                </button>
              </Dropdown>
            ) : (
              <button
                type="button"
                className="pet-header-icon-btn"
                onClick={() => setAuthMode('choice')}
                aria-label="Account"
              >
                <UserOutlined />
              </button>
            )}
          </div>
        </header>

        {isSearchRowAllowedPage && searchOpen && (
          <form className="pet-header-search-row" onSubmit={submitSearch}>
            <SearchOutlined />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('searchPlaceholder', 'Search for food, toys, accessories...')}
            />
          </form>
        )}

        <MobileNav
          open={navOpen}
          onClose={() => setNavOpen(false)}
          items={navItems}
          title={appName}
        />
        <StorefrontAuthModal
          open={Boolean(authMode)}
          mode={authMode ?? 'choice'}
          onCancel={() => {
            setAuthMode(null);
            setAuthRedirect(null);
          }}
          onAuthenticated={() => {
            if (authRedirect) navigate(authRedirect);
            setAuthRedirect(null);
          }}
        />
        <CheckoutModal key="checkout-modal" className="pet-checkout-modal" />
      </>
    );
  }

  // Checkout opens in the shared modal over the page; after a login it
  // reopens there instead of routing to /checkout.
  const goToCheckout = () => {
    if (isAuthenticated) {
      openCheckoutModal();
      return;
    }
    setAuthRedirect(CHECKOUT_MODAL_REDIRECT);
    setAuthMode('login');
  };

  return (
    <>
      <header className="pet-header pet-header-desktop">
        <div className="pet-header-top">
          {/* Container 1: Logo & Location */}
          <div className="pet-header-brand-group">
            <Link to="/" className="pet-header-logo">
              {runtimeLogoUrl || config?.logoUrl ? (
                <img
                  src={runtimeLogoUrl || config.logoUrl}
                  alt=""
                  style={{
                    width: '2.25rem',
                    height: '2.25rem',
                    objectFit: 'contain',
                    background: 'transparent',
                  }}
                />
              ) : (
                <span className="pet-header-paw" aria-hidden>
                  🐾
                </span>
              )}
            </Link>

            <button type="button" className="pet-header-location" onClick={openStoreSelector}>
              <img src="/location-pin.png" alt="Location" className="location-icon-img" />
              <span>{activeLocation?.name ?? ''}</span>
            </button>
          </div>

          {/* Container 2: Search Bar */}
          <div className="pet-header-search-group">
            <form
              className={`pet-header-search ${searchOpen ? 'open' : ''} ${shouldHideHeaderSearch ? 'is-hidden' : ''}`}
              onSubmit={submitSearch}
            >
              <input
                type="text"
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('searchPlaceholder', 'Search for food, toys, accessories...')}
              />
              <button type="submit" aria-label="Search">
                <SearchOutlined />
              </button>
            </form>
          </div>

          {/* Container 4: Icons & Utilities */}
          <div className="pet-header-utilities-group">
            <div className="pet-header-utilities">
              <button
                type="button"
                className="pet-header-utility pet-header-search-btn"
                onClick={() => setSearchOpen((prev) => !prev)}
                aria-label={t('search', 'Search')}
              >
                <SearchOutlined />
              </button>
              {utilityItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  className="pet-header-utility"
                  aria-label={item.label}
                >
                  {item.label === 'Wishlist' ? (
                    <Badge count={wishlistCount} size="small">
                      {UTILITY_ICONS[item.label]}
                    </Badge>
                  ) : (
                    (UTILITY_ICONS[item.label] ?? <HeartOutlined />)
                  )}
                </Link>
              ))}
              <button
                type="button"
                className="pet-header-utility pet-header-orders-btn"
                aria-label={t('myOrders', 'My Orders')}
                onClick={onOrdersClick}
              >
                <ShoppingOutlined />
              </button>

              <button
                type="button"
                className="pet-header-utility"
                onClick={() => setCartOpen(true)}
                aria-label={t('yourCart', 'Cart')}
              >
                <Badge count={lineCount} size="small">
                  <ShoppingCartOutlined />
                </Badge>
              </button>
              {isAuthenticated ? (
                <Dropdown
                  menu={{ items: accountItems, onClick: onAccountClick }}
                  trigger={['click']}
                >
                  <button
                    type="button"
                    className="pet-header-utility"
                    aria-label={t('account', 'Account')}
                  >
                    <Avatar
                      size={32}
                      className="pet-header-account-avatar"
                      style={{
                        ...getAvatarStyles(displayName),
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {displayName[0]?.toUpperCase()}
                    </Avatar>
                  </button>
                </Dropdown>
              ) : (
                <button
                  type="button"
                  className="pet-header-utility"
                  onClick={() => setAuthMode('choice')}
                  aria-label={t('account', 'Account')}
                >
                  <UserOutlined />
                </button>
              )}
            </div>
          </div>
        </div>
        {navItems.length > 0 ? (
          <nav className="pet-header-nav" aria-label="Pet categories">
            <div className="pet-header-nav-inner">
              <button
                type="button"
                className={`pet-header-collections-link ${collectionsOpen ? 'is-open' : ''}`}
                onClick={() => setCollectionsOpen((prev) => !prev)}
                aria-expanded={collectionsOpen}
                aria-controls="pet-header-collections-menu"
              >
                {collectionsOpen ? <CloseOutlined /> : <MenuOutlined />}
                <span>{t('shopByCategories', 'Shop by Categories')}</span>
              </button>
              {collectionsOpen ? (
                <div id="pet-header-collections-menu" className="pet-header-collections-menu">
                  {collectionItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="pet-header-collections-menu-link"
                      onClick={() => setCollectionsOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              ) : null}
              {desktopNavItems.map((item) => {
                const isActive =
                  item.to === '/'
                    ? pathname === '/'
                    : pathname === item.to || pathname.startsWith(`${item.to}/`);
                const className = `pet-header-nav-link ${isActive ? 'is-active' : ''}`;

                if (item.onClick) {
                  return (
                    <button
                      key={item.label}
                      type="button"
                      className={className}
                      onClick={item.onClick}
                    >
                      {item.label}
                    </button>
                  );
                }

                return (
                  <Link key={item.label} to={item.to} className={className}>
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </nav>
        ) : null}
      </header>
      <StorefrontAuthModal
        open={Boolean(authMode)}
        mode={authMode ?? 'choice'}
        onCancel={() => {
          setAuthMode(null);
          setAuthRedirect(null);
        }}
        onAuthenticated={() => {
          if (authRedirect === CHECKOUT_MODAL_REDIRECT) openCheckoutModal();
          else if (authRedirect) navigate(authRedirect);
          setAuthRedirect(null);
        }}
      />
      <CartDrawer open={cartOpen} onClose={closeCart} onCheckout={goToCheckout} classPrefix="pet" />
      {/* Same key in the phone branch above: the first render is always the
          phone layout (breakpoints unknown), and the key keeps this one modal
          instance, and its open state, across the switch. */}
      <CheckoutModal key="checkout-modal" className="pet-checkout-modal" />
    </>
  );
}
